import type { Directive, DirectiveBinding } from 'vue'
import { useAuthStore } from '@/modules/auth/stores/auth.store'

/**
 * Directiva v-role
 * Muestra u oculta un elemento basándose en el rol del usuario
 * Compatible con Vue 3 / Vuetify 3
 *
 * Uso:
 * - v-role="'ADMIN'" - Muestra solo si el usuario es ADMIN
 * - v-role="['ADMIN', 'ASSISTANT']" - Muestra si el usuario tiene alguno de los roles
 *
 * Modificadores:
 * - v-role.hide="'ADMIN'" - Oculta el elemento visualmente en lugar de eliminarlo del DOM
 * - v-role.not="'GUARDIAN'" - Muestra si el usuario NO tiene el rol especificado
 *
 * Ejemplos:
 * <v-btn v-role="'ADMIN'">Solo Admin</v-btn>
 * <v-list-item v-role="['ADMIN', 'ASSISTANT']">Admin o Asistente</v-list-item>
 * <div v-role.not="'GUARDIAN'">No visible para apoderados</div>
 */

function checkRole(el: HTMLElement, binding: DirectiveBinding<string | string[]>) {
  const authStore = useAuthStore()
  const userRole = authStore.userRole
  const requiredRoles = Array.isArray(binding.value) ? binding.value : [binding.value]

  const hasRequiredRole = requiredRoles.some(role => userRole === role)

  // Modificador .not invierte la lógica
  const shouldShow = binding.modifiers.not ? !hasRequiredRole : hasRequiredRole

  if (!shouldShow) {
    if (binding.modifiers.hide) {
      el.style.visibility = 'hidden'
      el.style.pointerEvents = 'none'
    } else {
      el.style.display = 'none'
    }
  } else {
    el.style.display = ''
    el.style.visibility = ''
    el.style.pointerEvents = ''
  }
}

export const vRole: Directive<HTMLElement, string | string[]> = {
  beforeMount(el: HTMLElement, binding: DirectiveBinding<string | string[]>) {
    checkRole(el, binding)
  },
  mounted(el: HTMLElement, binding: DirectiveBinding<string | string[]>) {
    checkRole(el, binding)
  },
  updated(el: HTMLElement, binding: DirectiveBinding<string | string[]>) {
    checkRole(el, binding)
  },
}

export const vHasRole = vRole
